import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollText, Search, ChevronLeft, ChevronRight, RefreshCw, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE = 50;

const ACTION_LABELS = {
  reveal: 'Reveal PII',
  view_detail: 'View (masked)',
  view_documents: 'View documents',
  document_sign: 'Sign document',
  document_view: 'View document',
  document_download: 'Download document',
  check_duplicate: 'Duplicate check',
  bulk_export: 'Bulk export',
  tax_report: 'Tax report',
  gst_report: 'GST report',
  gst_tcs_report: 'GST/TCS report',
  grant_create: 'Grant access',
  grant_revoke: 'Revoke access',
};

const ACTION_TONE = {
  reveal: 'bg-rose-50 text-rose-700 border-rose-200',
  document_download: 'bg-rose-50 text-rose-700 border-rose-200',
  bulk_export: 'bg-rose-50 text-rose-700 border-rose-200',
  grant_create: 'bg-amber-50 text-amber-700 border-amber-200',
  grant_revoke: 'bg-slate-100 text-slate-600 border-slate-200',
};

const ROLE_TONE = {
  admin: 'bg-violet-50 text-violet-700',
  accounts: 'bg-blue-50 text-blue-700',
  agent: 'bg-teal-50 text-teal-700',
  'signed-url': 'bg-slate-100 text-slate-500',
};

/**
 * Admin-only "who viewed what PII, when" audit view backed by
 * GET /api/admin/pii-access-log (collection pii_access_log).
 */
export default function PiiAccessLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [clientUcc, setClientUcc] = useState('');
  const [action, setAction] = useState('all');

  const load = useCallback(async (nextSkip = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE, skip: nextSkip };
      if (userEmail.trim()) params.userEmail = userEmail.trim();
      if (clientUcc.trim()) params.clientUcc = clientUcc.trim();
      if (action && action !== 'all') params.action = action;
      const r = await axios.get(`${BACKEND_URL}/api/admin/pii-access-log`, { params, withCredentials: true });
      setEntries(r.data?.entries || []);
      setTotal(r.data?.total || 0);
      setSkip(nextSkip);
    } catch {
      setEntries([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userEmail, clientUcc, action]);

  useEffect(() => { load(0); /* eslint-disable-next-line */ }, []);

  const fmt = (ts) => {
    try { return new Date(ts).toLocaleString('en-IN'); } catch { return ts; }
  };

  return (
    <Card className="p-6 rounded-2xl mb-6" data-testid="pii-access-log">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-rose-500" />
          PII Access Audit
        </h2>
        <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Admin only</span>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(0)}
            placeholder="Search by user email"
            className="h-9 pl-9"
            data-testid="audit-search-user"
          />
        </div>
        <Input
          value={clientUcc}
          onChange={(e) => setClientUcc(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(0)}
          placeholder="Traveller UCC"
          className="h-9 sm:w-40"
          data-testid="audit-search-ucc"
        />
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-9 sm:w-48" data-testid="audit-action-filter">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {Object.keys(ACTION_LABELS).map((a) => (
              <SelectItem key={a} value={a}>{ACTION_LABELS[a]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => load(0)} disabled={loading} className="h-9 bg-slate-900 hover:bg-slate-800 text-white" data-testid="audit-apply-btn">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Apply
        </Button>
      </div>

      <div className="overflow-x-auto border border-slate-100 rounded-xl">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-3 py-2 font-medium">When</th>
              <th className="text-left px-3 py-2 font-medium">User</th>
              <th className="text-left px-3 py-2 font-medium">Action</th>
              <th className="text-left px-3 py-2 font-medium">Traveller</th>
              <th className="text-left px-3 py-2 font-medium">Fields</th>
              <th className="text-left px-3 py-2 font-medium">Masked</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">{loading ? 'Loading…' : 'No access events found.'}</td></tr>
            ) : entries.map((e, i) => (
              <tr key={i} className="hover:bg-slate-50" data-testid={`audit-row-${i}`}>
                <td className="px-3 py-2 whitespace-nowrap text-slate-600 text-xs">{fmt(e.timestamp)}</td>
                <td className="px-3 py-2">
                  <div className="text-slate-800 truncate max-w-[180px]">{e.userEmail || e.userId || '—'}</div>
                  <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${ROLE_TONE[e.role] || 'bg-slate-100 text-slate-500'}`}>{e.role || '—'}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${ACTION_TONE[e.action] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {ACTION_LABELS[e.action] || e.action}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600">{e.clientUcc || '—'}{e.tripId ? ` · ${e.tripId}` : ''}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{(e.fields || []).join(', ') || '—'}{e.recordCount > 1 ? ` (${e.recordCount})` : ''}</td>
                <td className="px-3 py-2">
                  {e.masked
                    ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><EyeOff className="h-3.5 w-3.5" /> Masked</span>
                    : <span className="inline-flex items-center gap-1 text-rose-600 text-xs"><Eye className="h-3.5 w-3.5" /> Full</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <span data-testid="audit-total">{total} event{total === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8" disabled={skip === 0 || loading} onClick={() => load(Math.max(0, skip - PAGE))} data-testid="audit-prev">
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <span>{total === 0 ? 0 : skip + 1}–{Math.min(skip + PAGE, total)}</span>
          <Button variant="outline" size="sm" className="h-8" disabled={skip + PAGE >= total || loading} onClick={() => load(skip + PAGE)} data-testid="audit-next">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
